import { z } from 'zod';
import { entityIdSchema, isoDateTimeSchema, safeTextSchema } from '@ebu/contracts';
import { DECOR_COLOURS, portsOf, type WorldObjectType } from './objects';

/**
 * Renderer-independent world document, schema version 1. PlayCanvas (Phase 8) renders it; the
 * IndexedDB store and JSON export persist exactly this shape. Bump WORLD_SCHEMA_VERSION and add a
 * migration in migrate.ts for any incompatible change — never change v1 in place.
 */

export const WORLD_FORMAT = 'ebu-kids-world/world';
export const WORLD_SCHEMA_VERSION = 1;

export const WORLD_LIMITS = {
  /** Import file size limit, checked before parsing. */
  maxFileBytes: 512 * 1024,
  maxObjects: 300,
  maxConnections: 300,
  maxGameRefs: 20,
  /** Workshop half-extent in metres on each axis. */
  maxCoordinate: 50,
} as const;

export const localIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,64}$/, 'Expected a short local identifier');

const coordinate = z.number().min(-WORLD_LIMITS.maxCoordinate).max(WORLD_LIMITS.maxCoordinate);

const base = {
  id: localIdSchema,
  position: z.tuple([coordinate, coordinate, coordinate]),
  /** Degrees around the vertical axis, [0, 360). */
  rotationY: z.number().min(0).lt(360),
  scale: z.number().min(0.25).max(4),
};

const decorProps = z.strictObject({ colour: z.enum(DECOR_COLOURS) });
const emptyProps = z.strictObject({});

const propsByType = {
  'floor-tile': decorProps,
  'wall-panel': decorProps,
  table: decorProps,
  shelf: decorProps,
  plant: decorProps,
  rug: decorProps,
  window: decorProps,
  poster: z.strictObject({ colour: z.enum(DECOR_COLOURS), caption: safeTextSchema(40).nullable() }),
  battery: emptyProps,
  switch: z.strictObject({ closed: z.boolean() }),
  lamp: emptyProps,
  /** Simulated light level threshold (0 = dark, 1 = bright) at which the sensor conducts. */
  'light-sensor': z.strictObject({ threshold: z.number().min(0).max(1) }),
  fan: emptyProps,
} as const satisfies Record<WorldObjectType, z.ZodType>;

function objectSchemaFor<T extends WorldObjectType>(type: T) {
  return z.strictObject({ ...base, type: z.literal(type), props: propsByType[type] });
}

/** Listed explicitly so each member keeps its precise props type; a test checks completeness. */
export const worldObjectSchema = z.discriminatedUnion('type', [
  objectSchemaFor('floor-tile'),
  objectSchemaFor('wall-panel'),
  objectSchemaFor('table'),
  objectSchemaFor('shelf'),
  objectSchemaFor('plant'),
  objectSchemaFor('rug'),
  objectSchemaFor('window'),
  objectSchemaFor('poster'),
  objectSchemaFor('battery'),
  objectSchemaFor('switch'),
  objectSchemaFor('lamp'),
  objectSchemaFor('light-sensor'),
  objectSchemaFor('fan'),
]);
export type WorldObject = z.infer<typeof worldObjectSchema>;

const terminalSchema = z.strictObject({ objectId: localIdSchema, port: z.string().max(20) });

export const worldConnectionSchema = z.strictObject({
  id: localIdSchema,
  from: terminalSchema,
  to: terminalSchema,
});
export type WorldConnection = z.infer<typeof worldConnectionSchema>;

export const gameRefSchema = z.strictObject({
  missionId: entityIdSchema,
  objectiveId: z.string().regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/),
});

export const worldSchema = z
  .strictObject({
    format: z.literal(WORLD_FORMAT),
    schemaVersion: z.literal(WORLD_SCHEMA_VERSION),
    id: localIdSchema,
    title: safeTextSchema(60),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    objects: z.array(worldObjectSchema).max(WORLD_LIMITS.maxObjects),
    connections: z.array(worldConnectionSchema).max(WORLD_LIMITS.maxConnections),
    gameRefs: z.array(gameRefSchema).max(WORLD_LIMITS.maxGameRefs),
  })
  .superRefine((world, ctx) => {
    const types = new Map<string, WorldObjectType>();
    world.objects.forEach((o, i) => {
      if (types.has(o.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['objects', i, 'id'],
          message: 'Duplicate object id',
        });
      }
      types.set(o.id, o.type);
    });
    const connectionIds = new Set<string>();
    world.connections.forEach((c, i) => {
      if (connectionIds.has(c.id)) {
        ctx.addIssue({
          code: 'custom',
          path: ['connections', i, 'id'],
          message: 'Duplicate connection id',
        });
      }
      connectionIds.add(c.id);
      for (const end of ['from', 'to'] as const) {
        const type = types.get(c[end].objectId);
        if (!type) {
          ctx.addIssue({
            code: 'custom',
            path: ['connections', i, end, 'objectId'],
            message: 'Unknown object',
          });
        } else if (!portsOf(type).includes(c[end].port)) {
          ctx.addIssue({
            code: 'custom',
            path: ['connections', i, end, 'port'],
            message: `No such port on ${type}`,
          });
        }
      }
      if (c.from.objectId === c.to.objectId && c.from.port === c.to.port) {
        ctx.addIssue({
          code: 'custom',
          path: ['connections', i],
          message: 'A wire cannot connect a port to itself',
        });
      }
    });
    if (Date.parse(world.updatedAt) < Date.parse(world.createdAt)) {
      ctx.addIssue({
        code: 'custom',
        path: ['updatedAt'],
        message: 'updatedAt is before createdAt',
      });
    }
  });
export type World = z.infer<typeof worldSchema>;

export function createEmptyWorld(input: { id: string; title: string; now: Date }): World {
  const at = input.now.toISOString();
  return worldSchema.parse({
    format: WORLD_FORMAT,
    schemaVersion: WORLD_SCHEMA_VERSION,
    id: input.id,
    title: input.title,
    createdAt: at,
    updatedAt: at,
    objects: [],
    connections: [],
    gameRefs: [],
  });
}
